import html_default from "./template.default.html?raw";
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("default" == app_theme) {
  html = html_default;
}
import { computed, inject, onMounted, onUnmounted, ref, watch, type SetupContext } from "vue";
import type { AddressService } from "../../application/services/AddressService.ts";
import type { AddressCheckState, AddressInput, AddressSuggestionDto, LocalityDto } from "../../shared/AddressDtos.ts";

interface Props {
  modelValue: AddressInput;
  idPrefix?: string;
  required?: boolean;
  disabled?: boolean;
}

const COUNTRIES = [
  { code: "CH", name: "Schweiz" },
  { code: "DE", name: "Deutschland" },
  { code: "AT", name: "Österreich" },
  { code: "LI", name: "Liechtenstein" },
];

// Adress-Assistent: Land, PLZ -> Ort, Strasse mit Vorschlaegen (Hausnummer inklusive), Pruefbadge. Nie blockierend.
export default {
  template: html,
  emits: {
    "update:modelValue": (value: AddressInput) => typeof value === "object",
    onVerified: (state: AddressCheckState) => typeof state === "string",
  },
  props: {
    modelValue: { type: Object, required: true },
    idPrefix: { type: String, default: "addr" },
    required: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
  },
  setup(props: Props, { emit }: SetupContext) {
    const addressService: AddressService | undefined = inject("addressService");
    const countries = COUNTRIES;
    const localities = ref<LocalityDto[]>([]);
    const suggestions = ref<AddressSuggestionDto[]>([]);
    const suggestionsOpen = ref(false);
    const activeIndex = ref(-1);
    const checkState = ref<AddressCheckState>("notChecked");
    const checkMessage = ref("");
    const streetInput = ref<HTMLInputElement | null>(null);
    let suggestTimer: number | undefined;
    let suggestAbort: AbortController | undefined;
    let verifyTimer: number | undefined;
    let lastZipLookup = "";

    const model = computed({
      get: () => props.modelValue,
      set: (v: AddressInput) => emit("update:modelValue", v),
    });

    const patch = function (changes: Partial<AddressInput>) {
      model.value = { ...props.modelValue, ...changes };
    };

    const lookupSupported = computed(() => ["CH", "DE", "AT", "LI"].includes((props.modelValue.countryCode || "").toUpperCase()));
    const zipLength = computed(() => (props.modelValue.countryCode == "DE" ? 5 : 4));

    // PLZ -> Ort
    // Der neue Wert kommt als Parameter mit: das v-model des Elternteils ist im selben Tick noch nicht aktualisiert.
    const onZipChanged = async function (zipValue?: string) {
      const zip = (zipValue ?? props.modelValue.zipCode ?? "").trim();
      const key = `${props.modelValue.countryCode}|${zip}`;
      if (zip.length < zipLength.value || !addressService || !lookupSupported.value || key == lastZipLookup) {
        if (zip.length < zipLength.value) {
          localities.value = [];
        }
        return;
      }
      lastZipLookup = key;
      const result = await addressService.localities(props.modelValue.countryCode, zip);
      localities.value = result;
      if (result.length >= 1) {
        const current = (props.modelValue.city || "").trim().toLowerCase();
        const stillValid = result.some((l) => l.city.toLowerCase() == current);
        if (!stillValid) {
          patch({ zipCode: zip, city: result[0].city });
        }
      }
      scheduleVerify();
    };

    // Strasse -> Vorschlaege (debounced, laufende Anfrage abbrechen)
    const onStreetInput = function () {
      checkState.value = "notChecked";
      if (suggestTimer) {
        window.clearTimeout(suggestTimer);
      }
      suggestTimer = window.setTimeout(loadSuggestions, 250);
    };

    const loadSuggestions = async function () {
      const q = (props.modelValue.streetName || "").trim();
      if (!addressService || !lookupSupported.value || q.length < 2) {
        suggestions.value = [];
        suggestionsOpen.value = false;
        return;
      }
      suggestAbort?.abort();
      suggestAbort = new AbortController();
      try {
        const result = await addressService.suggest(props.modelValue.countryCode, q, props.modelValue.zipCode || "", props.modelValue.city || "", suggestAbort.signal);
        suggestions.value = result;
        suggestionsOpen.value = result.length > 0;
        activeIndex.value = -1;
      } catch {
        // abgebrochen
      }
    };

    const pickSuggestion = function (s: AddressSuggestionDto) {
      patch({
        streetName: s.street,
        buildingNumber: s.number || props.modelValue.buildingNumber,
        zipCode: s.zipCode || props.modelValue.zipCode,
        city: s.city || props.modelValue.city,
      });
      suggestions.value = [];
      suggestionsOpen.value = false;
      checkState.value = s.number ? "verified" : "notChecked";
      checkMessage.value = s.number ? "Adresse geprüft" : "";
      if (!s.number) {
        scheduleVerify();
      }
      emit("onVerified", checkState.value);
    };

    const onStreetKeydown = function (e: KeyboardEvent) {
      if (!suggestionsOpen.value) {
        return;
      }
      if (e.key == "ArrowDown") {
        e.preventDefault();
        activeIndex.value = Math.min(activeIndex.value + 1, suggestions.value.length - 1);
      } else if (e.key == "ArrowUp") {
        e.preventDefault();
        activeIndex.value = Math.max(activeIndex.value - 1, 0);
      } else if (e.key == "Enter") {
        if (activeIndex.value >= 0) {
          e.preventDefault();
          pickSuggestion(suggestions.value[activeIndex.value]);
        }
      } else if (e.key == "Escape") {
        suggestionsOpen.value = false;
      }
    };

    const closeSuggestionsSoon = function () {
      // Klick auf einen Vorschlag muss vor dem Schliessen ankommen
      window.setTimeout(() => (suggestionsOpen.value = false), 150);
      scheduleVerify();
    };

    // Pruefung (nie blockierend)
    const scheduleVerify = function () {
      if (verifyTimer) {
        window.clearTimeout(verifyTimer);
      }
      verifyTimer = window.setTimeout(verifyNow, 400);
    };

    const verifyNow = async function () {
      const m = props.modelValue;
      if (!addressService || !lookupSupported.value || !(m.streetName || "").trim() || !(m.zipCode || "").trim()) {
        checkState.value = "notChecked";
        checkMessage.value = "";
        return;
      }
      const res = await addressService.verify({
        countryCode: m.countryCode,
        streetName: m.streetName,
        buildingNumber: m.buildingNumber || "",
        zipCode: m.zipCode,
        city: m.city || "",
      });
      checkState.value = res.state;
      checkMessage.value = res.state == "verified" ? "Adresse geprüft" : res.message;
      emit("onVerified", res.state);
    };

    watch(() => props.modelValue.countryCode, function () {
      localities.value = [];
      lastZipLookup = "";
      checkState.value = "notChecked";
      void onZipChanged();
    });

    onMounted(function () {
      if ((props.modelValue.zipCode || "").length >= 4) {
        void onZipChanged();
      }
    });

    onUnmounted(function () {
      if (suggestTimer) window.clearTimeout(suggestTimer);
      if (verifyTimer) window.clearTimeout(verifyTimer);
      suggestAbort?.abort();
    });

    return {
      props,
      countries,
      localities,
      suggestions,
      suggestionsOpen,
      activeIndex,
      checkState,
      checkMessage,
      streetInput,
      lookupSupported,
      patch,
      onZipChanged,
      onStreetInput,
      onStreetKeydown,
      pickSuggestion,
      closeSuggestionsSoon,
      scheduleVerify,
    };
  },
};
